/* ============================================================
   Dathent — Admin Session Replay (/app/admin/replay)
   TASK-168 (#274), child of EPIC-018 (#200). Plays back the mouse/click/
   scroll/route stream pages/dathent_ai src/utils/uxTracker.js already
   records into ux_sessions + ux_event_batches (TASK-071, migration 0098),
   read here through dathent-api-gateway-worker's GET /api/admin/replay/
   {sessions,session/:id,patterns} (src/routes/admin-replay.ts). Gated
   server-side by the same requireAdminSession() admin-behavior.ts uses;
   this screen carries no client guard of its own beyond the route-level
   <RequireAdmin> in App.jsx, mirroring AdminBehavior.jsx/AdminUsers.jsx.

   No charting/animation library — a hand-rolled canvas-free player (plain
   DOM + inline styles) driving a requestAnimationFrame loop, same
   "no heavy dependencies" rule as the sibling admin screens.

   PLAYER LIMITATIONS (from the data uxTracker.js actually captures — see
   that file's header comment):
   - There is no DOM/screenshot snapshot. The viewport is a schematic
     rectangle sized from the session's ONE-TIME viewportW/viewportH
     (captured at session start only — no resize events), or a fixed
     1440x900 placeholder when that data is missing, clearly labeled either
     way.
   - Mouse/click coordinates are PAGE coordinates (e.pageX/pageY — include
     document scroll), not viewport-relative. The player compensates using
     the most recent 'sc' event's scrollY (vertical only — there is no
     scrollX signal at all, so horizontal scroll is not compensated and the
     cursor can drift outside the box on a horizontally-scrolled page).
   - Scroll is sampled at most once per second and mouse at 2Hz, so the
     replay is a reconstruction from samples, not a frame-accurate capture.
   - There is no "what did this click actually do" signal (no DOM mutation
     / navigation-caused-by-this-click tracking), which is also why the
     Patterns tab cannot report dead clicks — see that tab's note.
   ============================================================ */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, DathentAPIError } from '../services/api/client.js';

const DEFAULT_SESSIONS_LIMIT = 50;
const FALLBACK_VIEWPORT = { w: 1440, h: 900 };
const RIPPLE_MS = 650;
const EVENT_WINDOW_BEFORE = 40;
const EVENT_WINDOW_AFTER = 15;

function isoDay(date) {
  return date.toISOString().slice(0, 10);
}
function daysAgo(n) {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - n);
  return isoDay(d);
}
const PRESETS = [
  { key: '1d', label: '24h', from: () => daysAgo(1), to: () => daysAgo(0) },
  { key: '7d', label: '7d', from: () => daysAgo(6), to: () => daysAgo(0) },
  { key: '30d', label: '30d', from: () => daysAgo(29), to: () => daysAgo(0) },
];

function errMsg(e) {
  if (e instanceof DathentAPIError) return e.message || 'Request failed';
  return e?.message || 'Request failed';
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function fmtDuration(ms) {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// ─── style primitives (mirrors AdminBehavior.jsx) ──────────────────────────

const card = {
  background: 'var(--surface, #16161a)',
  border: '1px solid var(--border, #2a2a30)',
  borderRadius: 14,
  padding: 20,
};
const label = { display: 'block', fontSize: 12, color: 'var(--text-muted, #9a9aa2)', margin: '10px 0 4px' };
const input = {
  width: '100%', boxSizing: 'border-box', background: 'var(--surface-2, #0f0f12)',
  border: '1px solid var(--border, #2a2a30)', borderRadius: 10, color: 'var(--text, #ececf0)',
  padding: '9px 11px', fontSize: 13.5, fontFamily: 'inherit',
};
const btn = (disabled) => ({
  background: 'var(--gold, #caa24a)', color: '#0b0b0c', border: 0, borderRadius: 10,
  padding: '9px 16px', fontSize: 13.5, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
});
const btnGhost = (disabled, active) => ({
  background: active ? 'var(--surface-2, #0f0f12)' : 'transparent',
  color: active ? 'var(--text, #ececf0)' : 'var(--text-muted, #9a9aa2)',
  border: `1px solid ${active ? 'var(--gold, #caa24a)' : 'var(--border, #2a2a30)'}`,
  borderRadius: 10, padding: '7px 13px', fontSize: 12.5, fontWeight: 500,
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
});
const tabBtn = (active) => ({
  background: active ? 'var(--surface, #16161a)' : 'transparent',
  color: active ? 'var(--text, #ececf0)' : 'var(--text-muted, #9a9aa2)',
  border: '1px solid var(--border, #2a2a30)',
  borderBottom: active ? '1px solid var(--surface, #16161a)' : '1px solid var(--border, #2a2a30)',
  borderRadius: '10px 10px 0 0', padding: '9px 18px', fontSize: 13.5, fontWeight: 600,
  cursor: 'pointer', marginBottom: -1,
});

function ErrorNote({ error }) {
  if (!error) return null;
  return (
    <div style={{
      marginTop: 12, padding: '10px 12px', borderRadius: 10, fontSize: 13,
      background: '#e0685f18', border: '1px solid #e0685f55', color: '#e8a59f',
    }}>{error}</div>
  );
}

// ─── filters (period + user) ───────────────────────────────────────────────

function FiltersPanel({ from, to, setFrom, setTo, activePreset, onPreset, userId, setUserId, disabled }) {
  return (
    <div style={{ ...card, marginBottom: 14 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
        <div>
          <label style={label}>Period</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {PRESETS.map((p) => (
              <button key={p.key} type="button" style={btnGhost(disabled, activePreset === p.key)} disabled={disabled} onClick={() => onPreset(p)}>
                {p.label}
              </button>
            ))}
            <input type="date" style={{ ...input, width: 150 }} value={from} onChange={(e) => setFrom(e.target.value)} disabled={disabled} />
            <span style={{ color: 'var(--text-muted, #9a9aa2)', fontSize: 13 }}>→</span>
            <input type="date" style={{ ...input, width: 150 }} value={to} onChange={(e) => setTo(e.target.value)} disabled={disabled} />
          </div>
        </div>
        {setUserId && (
          <div>
            <label style={label}>User ID</label>
            <input style={{ ...input, width: 220 }} placeholder="filter by user_id…" value={userId} onChange={(e) => setUserId(e.target.value)} disabled={disabled} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── sessions list ──────────────────────────────────────────────────────────

function SessionsList({ sessions, loading, error, total, selectedId, onSelect }) {
  const th = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted, #9a9aa2)', padding: '0 8px 8px', whiteSpace: 'nowrap' };
  const td = { padding: '8px', fontSize: 12.5, borderTop: '1px solid var(--border, #2a2a30)' };

  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: 620 }}>
      <div style={{ padding: '14px 16px 0', fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between' }}>
        <span>Sessions</span>
        <span style={{ color: 'var(--text-muted, #9a9aa2)', fontWeight: 400 }}>{total} total in range</span>
      </div>
      <div style={{ overflowY: 'auto', overflowX: 'auto' }}>
        {loading ? (
          <div style={{ padding: 20, color: 'var(--text-muted, #9a9aa2)' }}>Loading sessions…</div>
        ) : error ? (
          <div style={{ padding: '0 16px 16px' }}><ErrorNote error={error} /></div>
        ) : sessions.length === 0 ? (
          <div style={{ padding: 20, color: 'var(--text-muted, #9a9aa2)' }}>No sessions match this range.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
            <thead>
              <tr>
                <th style={{ ...th, paddingLeft: 16 }}>Start</th>
                <th style={th}>Duration</th>
                <th style={th}>User</th>
                <th style={th}>Entry</th>
                <th style={{ ...th, paddingRight: 16 }}>Events</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                const active = s.id === selectedId;
                return (
                  <tr
                    key={s.id}
                    onClick={() => onSelect(s.id)}
                    style={{ cursor: 'pointer', background: active ? 'var(--accent-soft, #caa24a22)' : 'transparent' }}
                  >
                    <td style={{ ...td, paddingLeft: 16, whiteSpace: 'nowrap', color: 'var(--text-muted, #9a9aa2)' }}>{fmtDateTime(s.startedAt)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDuration(s.durationMs)}</td>
                    <td style={{ ...td, color: 'var(--text-muted, #9a9aa2)' }}>{s.userId || '—'}</td>
                    <td style={{ ...td, color: 'var(--text-muted, #9a9aa2)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.entryPath || ''}>{s.entryPath || '—'}</td>
                    <td style={{ ...td, paddingRight: 16, whiteSpace: 'nowrap' }}>{s.events} <span style={{ color: 'var(--text-muted, #9a9aa2)' }}>({s.batches}b)</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── timeline helpers ───────────────────────────────────────────────────────

function buildTimeline(batches) {
  const events = [];
  for (const b of batches || []) {
    for (const e of b.events || []) {
      if (e && typeof e.ts === 'number') events.push(e);
    }
  }
  events.sort((a, b) => a.ts - b.ts);
  const startTs = events.length ? events[0].ts : 0;
  const endTs = events.length ? events[events.length - 1].ts : 0;
  const positions = events
    .filter((e) => (e.t === 'mv' || e.t === 'cl') && typeof e.x === 'number' && typeof e.y === 'number')
    .map((e) => ({ ts: e.ts, x: e.x, y: e.y }));
  const clicks = events.filter((e) => e.t === 'cl' && typeof e.x === 'number' && typeof e.y === 'number');
  const scrolls = events.filter((e) => e.t === 'sc' && typeof e.y === 'number');
  const pageviews = events.filter((e) => e.t === 'pv' && typeof e.path === 'string');
  return { events, startTs, endTs, totalMs: Math.max(0, endTs - startTs), positions, clicks, scrolls, pageviews };
}

// Index of the last entry whose ts <= target (arr sorted ascending by ts). -1 if none.
function lastAtOrBefore(arr, targetTs) {
  let lo = 0;
  let hi = arr.length - 1;
  let res = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid].ts <= targetTs) { res = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return res;
}

function describeEvent(e) {
  switch (e.t) {
    case 'pv': return `→ ${e.path || '(unknown path)'}`;
    case 'mv': return `move (${Math.round(e.x)}, ${Math.round(e.y)})`;
    case 'cl': return `click (${Math.round(e.x)}, ${Math.round(e.y)})${e.el ? ` — ${e.el}` : ''}`;
    case 'sc': return `scroll y=${Math.round(e.y)}`;
    case 'foc': return `focus: ${e.field || '(unnamed)'}`;
    case 'vis': return `visibility: ${e.s || '?'}`;
    case 'ev': return `event: ${e.name || '?'}${e.d ? ` ${JSON.stringify(e.d)}` : ''}`;
    default: return e.t || '(unknown)';
  }
}

const EVENT_COLOR = {
  pv: '#7fb8e8', mv: '#6a6a72', cl: '#e0685f', sc: '#9fd67e', foc: '#c99ee8', vis: '#e7c878', ev: '#caa24a',
};

// ─── player ─────────────────────────────────────────────────────────────────

function Player({ session, timeline }) {
  const [relMs, setRelMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const rafRef = useRef(null);
  const lastFrameRef = useRef(null);
  const activeItemRef = useRef(null);

  // Reset playback whenever a new session's timeline loads.
  useEffect(() => {
    setRelMs(0);
    setPlaying(false);
  }, [timeline]);

  useEffect(() => {
    if (!playing) return undefined;
    lastFrameRef.current = null;
    const tick = (now) => {
      if (lastFrameRef.current == null) lastFrameRef.current = now;
      const delta = now - lastFrameRef.current;
      lastFrameRef.current = now;
      setRelMs((cur) => {
        const next = cur + delta * speed;
        if (next >= timeline.totalMs) {
          setPlaying(false);
          return timeline.totalMs;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [playing, speed, timeline.totalMs]);

  const absTs = timeline.startTs + relMs;

  const posIdx = lastAtOrBefore(timeline.positions, absTs);
  const cursor = posIdx >= 0 ? timeline.positions[posIdx] : null;
  const scrollIdx = lastAtOrBefore(timeline.scrolls, absTs);
  const scrollY = scrollIdx >= 0 ? timeline.scrolls[scrollIdx].y : 0;
  const pvIdx = lastAtOrBefore(timeline.pageviews, absTs);
  const currentPath = pvIdx >= 0 ? timeline.pageviews[pvIdx].path : session.entryPath;

  const activeRipples = timeline.clicks.filter((c) => absTs - c.ts >= 0 && absTs - c.ts <= RIPPLE_MS);

  const hasViewport = Number.isFinite(session.viewportW) && Number.isFinite(session.viewportH) && session.viewportW > 0 && session.viewportH > 0;
  const vw = hasViewport ? session.viewportW : FALLBACK_VIEWPORT.w;
  const vh = hasViewport ? session.viewportH : FALLBACK_VIEWPORT.h;
  const boxMaxWidth = 620;
  const scale = Math.min(1, boxMaxWidth / vw);
  const boxW = vw * scale;
  const boxH = vh * scale;

  const activeEventIdx = lastAtOrBefore(timeline.events, absTs);

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeEventIdx]);

  const sliceStart = Math.max(0, activeEventIdx - EVENT_WINDOW_BEFORE);
  const sliceEnd = Math.min(timeline.events.length, activeEventIdx + 1 + EVENT_WINDOW_AFTER);
  const eventSlice = timeline.events.slice(sliceStart, sliceEnd);

  const onScrub = (e) => {
    setPlaying(false);
    setRelMs(Number(e.target.value));
  };

  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
      <div style={{ ...card, flex: '1 1 380px', minWidth: 340 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Player</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted, #9a9aa2)', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
            {hasViewport ? `${vw}×${vh} (recorded)` : `${vw}×${vh} (assumed — no viewport data)`}
          </div>
        </div>

        <div style={{
          fontSize: 12, marginBottom: 8, padding: '5px 10px', borderRadius: 8,
          background: 'var(--surface-2, #0f0f12)', border: '1px solid var(--border, #2a2a30)',
          color: 'var(--text, #ececf0)', display: 'inline-block', maxWidth: '100%',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {currentPath || '(no route captured yet)'}
        </div>

        <div
          style={{
            position: 'relative', width: boxW, height: boxH, margin: '0 auto',
            background: 'var(--surface-2, #0f0f12)', border: '1px solid var(--border, #2a2a30)',
            borderRadius: 8, overflow: 'hidden',
          }}
        >
          {/* Faint scroll indicator: how far down the recorded page we are. */}
          <div style={{ position: 'absolute', top: 4, right: 6, fontSize: 9.5, color: 'var(--text-subtle, #6a6a72)', fontFamily: 'ui-monospace, monospace' }}>
            scrollY {Math.round(scrollY)}
          </div>

          {activeRipples.map((c, i) => {
            const age = absTs - c.ts;
            const t = Math.min(1, age / RIPPLE_MS);
            const r = 6 + t * 22;
            const opacity = 1 - t;
            return (
              <div
                key={`${c.ts}-${i}`}
                style={{
                  position: 'absolute',
                  left: c.x * scale - r,
                  top: (c.y - scrollY) * scale - r,
                  width: r * 2, height: r * 2, borderRadius: '50%',
                  border: `2px solid #e0685f`,
                  opacity,
                  pointerEvents: 'none',
                }}
              />
            );
          })}

          {cursor && (
            <div
              style={{
                position: 'absolute',
                left: cursor.x * scale - 5,
                top: (cursor.y - scrollY) * scale - 5,
                width: 10, height: 10, borderRadius: '50%',
                background: 'var(--gold, #caa24a)',
                boxShadow: '0 0 0 2px rgba(0,0,0,0.35)',
                pointerEvents: 'none',
              }}
            />
          )}

          {!cursor && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-subtle, #6a6a72)', fontSize: 12 }}>
              No cursor data yet
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <button type="button" style={btn(timeline.totalMs === 0)} disabled={timeline.totalMs === 0} onClick={() => setPlaying((p) => !p)}>
            {playing ? 'Pause' : 'Play'}
          </button>
          {[1, 4, 16].map((sp) => (
            <button key={sp} type="button" style={btnGhost(false, speed === sp)} onClick={() => setSpeed(sp)}>×{sp}</button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-muted, #9a9aa2)', fontFamily: 'ui-monospace, monospace' }}>
            {fmtDuration(relMs)} / {fmtDuration(timeline.totalMs)}
          </span>
        </div>
        <input
          type="range" min={0} max={Math.max(1, timeline.totalMs)} value={Math.min(relMs, timeline.totalMs)}
          onChange={onScrub}
          style={{ width: '100%', marginTop: 10 }}
          disabled={timeline.totalMs === 0}
        />
      </div>

      <div style={{ ...card, flex: '1 1 300px', minWidth: 280, maxHeight: 460, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          Events <span style={{ fontWeight: 400, color: 'var(--text-muted, #9a9aa2)' }}>({timeline.events.length} total)</span>
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {sliceStart > 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-subtle, #6a6a72)', padding: '4px 0' }}>… {sliceStart} earlier events</div>
          )}
          {eventSlice.map((e, i) => {
            const idx = sliceStart + i;
            const active = idx === activeEventIdx;
            return (
              <div
                key={`${e.ts}-${idx}`}
                ref={active ? activeItemRef : null}
                style={{
                  display: 'flex', gap: 8, alignItems: 'baseline', padding: '4px 6px', borderRadius: 6,
                  background: active ? 'var(--accent-soft, #caa24a22)' : 'transparent',
                  fontSize: 12,
                }}
              >
                <span style={{
                  width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                  background: EVENT_COLOR[e.t] || '#6a6a72', marginTop: 3,
                }} />
                <span style={{ color: 'var(--text-muted, #9a9aa2)', fontFamily: 'ui-monospace, monospace', fontSize: 10.5, whiteSpace: 'nowrap' }}>
                  +{fmtDuration(e.ts - timeline.startTs)}
                </span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{describeEvent(e)}</span>
              </div>
            );
          })}
          {sliceEnd < timeline.events.length && (
            <div style={{ fontSize: 11, color: 'var(--text-subtle, #6a6a72)', padding: '4px 0' }}>… {timeline.events.length - sliceEnd} later events</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── patterns tab ───────────────────────────────────────────────────────────

function PatternsTab() {
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(daysAgo(0));
  const [activePreset, setActivePreset] = useState('7d');
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const load = useCallback(async () => {
    setState((cur) => ({ ...cur, loading: true, error: null }));
    try {
      const res = await api.adminReplay.patterns({ from, to });
      setState({ loading: false, error: null, data: res });
    } catch (e) {
      setState((cur) => ({ ...cur, loading: false, error: errMsg(e) }));
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const onPreset = (p) => {
    setActivePreset(p.key);
    setFrom(p.from());
    setTo(p.to());
  };

  const data = state.data;
  const th = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted, #9a9aa2)', padding: '0 10px 8px', whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: 13, borderTop: '1px solid var(--border, #2a2a30)' };

  return (
    <div>
      <FiltersPanel from={from} to={to} setFrom={(v) => { setActivePreset(null); setFrom(v); }} setTo={(v) => { setActivePreset(null); setTo(v); }} activePreset={activePreset} onPreset={onPreset} disabled={state.loading} />

      {state.error && <ErrorNote error={state.error} />}

      {data && (
        <>
          {data.truncated && (
            <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 10, fontSize: 13, background: '#e7c87818', border: '1px solid #e7c87855', color: '#e7c878' }}>
              Scanned the first {data.batchesScanned} batches in range and stopped there — more exist. This report is a sample, not the full period.
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ ...card, flex: '1 1 160px' }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted, #9a9aa2)' }}>Rage-click clusters</div>
              <div style={{ fontSize: 24, fontWeight: 600, marginTop: 4 }}>{data.rageClicks.totalClusters}</div>
            </div>
            <div style={{ ...card, flex: '1 1 160px' }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted, #9a9aa2)' }}>Sessions scanned</div>
              <div style={{ fontSize: 24, fontWeight: 600, marginTop: 4 }}>{data.sessionsScanned}</div>
            </div>
            <div style={{ ...card, flex: '1 1 160px' }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted, #9a9aa2)' }}>Batches scanned</div>
              <div style={{ fontSize: 24, fontWeight: 600, marginTop: 4 }}>{data.batchesScanned}</div>
            </div>
          </div>

          <div style={{ ...card, padding: 0, overflowX: 'auto', marginBottom: 16 }}>
            <div style={{ padding: '14px 20px 0', fontSize: 13, fontWeight: 600 }}>Top pages by rage-click sessions</div>
            {data.rageClicks.topPages.length === 0 ? (
              <div style={{ padding: 20, color: 'var(--text-muted, #9a9aa2)' }}>No rage clicks detected in this range.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
                <thead>
                  <tr>
                    <th style={{ ...th, paddingLeft: 20 }}>Page</th>
                    <th style={th}>Sessions with rage</th>
                    <th style={{ ...th, paddingRight: 20 }}>Clusters</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rageClicks.topPages.map((p, i) => (
                    <tr key={i}>
                      <td style={{ ...td, paddingLeft: 20 }}>{p.path || '(unknown page)'}</td>
                      <td style={td}>{p.sessionsWithRage}</td>
                      <td style={{ ...td, paddingRight: 20 }}>{p.clusters}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={{ ...card, color: 'var(--text-muted, #9a9aa2)', fontSize: 13 }}>
            <strong style={{ color: 'var(--text, #ececf0)' }}>Dead clicks: </strong>
            not detected — {data.deadClicks.reason}
          </div>
        </>
      )}
    </div>
  );
}

// ─── screen shell ───────────────────────────────────────────────────────────

export function AdminReplay() {
  const [tab, setTab] = useState('sessions');

  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(daysAgo(0));
  const [activePreset, setActivePreset] = useState('7d');
  const [userId, setUserId] = useState('');

  const [sessionsState, setSessionsState] = useState({ loading: true, error: null, sessions: [], total: 0 });
  const [selectedId, setSelectedId] = useState(null);
  const [detailState, setDetailState] = useState({ loading: false, error: null, session: null, timeline: null });

  const loadSessions = useCallback(async () => {
    setSessionsState((cur) => ({ ...cur, loading: true, error: null }));
    try {
      const res = await api.adminReplay.sessions({ from, to, userId: userId || undefined, limit: DEFAULT_SESSIONS_LIMIT });
      setSessionsState({ loading: false, error: null, sessions: res.sessions || [], total: res.total ?? 0 });
    } catch (e) {
      setSessionsState((cur) => ({ ...cur, loading: false, error: errMsg(e) }));
    }
  }, [from, to, userId]);

  useEffect(() => { if (tab === 'sessions') loadSessions(); }, [tab, loadSessions]);

  const loadDetail = useCallback(async (id) => {
    setDetailState({ loading: true, error: null, session: null, timeline: null });
    try {
      const res = await api.adminReplay.session(id);
      setDetailState({ loading: false, error: null, session: res.session, timeline: buildTimeline(res.batches) });
    } catch (e) {
      setDetailState({ loading: false, error: errMsg(e), session: null, timeline: null });
    }
  }, []);

  const onSelect = (id) => {
    setSelectedId(id);
    loadDetail(id);
  };

  const onPreset = (p) => {
    setActivePreset(p.key);
    setFrom(p.from());
    setTo(p.to());
  };

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '8px 4px 64px' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--gold, #caa24a)' }}>Admin · internal</div>
        <h1 style={{ margin: '4px 0 2px', fontSize: 26 }}>Session replay</h1>
        <p style={{ margin: 0, color: 'var(--text-muted, #9a9aa2)', fontSize: 14 }}>
          Replays the mouse/click/scroll/route stream ux-tracker records — no DOM snapshot, schematic viewport only.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 0 }}>
        <button type="button" style={tabBtn(tab === 'sessions')} onClick={() => setTab('sessions')}>Sessions</button>
        <button type="button" style={tabBtn(tab === 'patterns')} onClick={() => setTab('patterns')}>Patterns</button>
      </div>
      <div style={{ borderTop: '1px solid var(--border, #2a2a30)', marginBottom: 16 }} />

      {tab === 'sessions' ? (
        <>
          <FiltersPanel
            from={from} to={to}
            setFrom={(v) => { setActivePreset(null); setFrom(v); }}
            setTo={(v) => { setActivePreset(null); setTo(v); }}
            activePreset={activePreset} onPreset={onPreset}
            userId={userId} setUserId={setUserId}
            disabled={sessionsState.loading}
          />

          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 420px', minWidth: 340 }}>
              <SessionsList
                sessions={sessionsState.sessions}
                loading={sessionsState.loading}
                error={sessionsState.error}
                total={sessionsState.total}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            </div>

            <div style={{ flex: '2 1 620px', minWidth: 320 }}>
              {!selectedId ? (
                <div style={{ ...card, color: 'var(--text-muted, #9a9aa2)', textAlign: 'center' }}>Select a session to replay it.</div>
              ) : detailState.loading ? (
                <div style={{ ...card, color: 'var(--text-muted, #9a9aa2)', textAlign: 'center' }}>Loading session…</div>
              ) : detailState.error ? (
                <ErrorNote error={detailState.error} />
              ) : detailState.session ? (
                <Player session={detailState.session} timeline={detailState.timeline} />
              ) : null}
            </div>
          </div>
        </>
      ) : (
        <PatternsTab />
      )}
    </div>
  );
}

export default AdminReplay;
